import React, { useState, useEffect } from 'react';
import '../components/Branch.css'; // Make sure your styles are here

const Branch = () => {
    const [branches, setBranches] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [branchName, setBranchName] = useState('');
    const [editingBranchId, setEditingBranchId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // 🔥 Helper function to get auth token from storage (using session_token as per login)
    const getAuthToken = () => {
        return localStorage.getItem('session_token');
    };

    // Fetch all branches on mount
    useEffect(() => {
        fetchBranches();
    }, []);

    const fetchBranches = async () => {
        try {
            const token = getAuthToken();
            const url = token 
                ? `https://newerp.awasthivitality.com/api/branch/show?token=${encodeURIComponent(token)}`
                : 'https://newerp.awasthivitality.com/api/branch/show';
            
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            const result = await response.json();

            if (result.success) {
                const formatted = result.data.map(branch => ({
                    id: branch.branch_id,
                    name: branch.branch_name,
                }));
                setBranches(formatted);
            } else {
                throw new Error('Failed to fetch branches');
            }
        } catch (err) {
            console.error(err);
            setError('Failed to load branches');
        }
    };

    const openAddModal = () => {
        setBranchName('');
        setEditingBranchId(null);
        setShowModal(true);
        setError(null);
    };

    const openEditModal = (branch) => {
        setBranchName(branch.name);
        setEditingBranchId(branch.id);
        setShowModal(true);
        setError(null);
    };

    const closeModal = () => {
        setShowModal(false);
        setBranchName('');
        setEditingBranchId(null);
        setError(null);
    };

const handleSubmit = async (e) => {
    e.preventDefault();

    if (!branchName.trim()) {
        setError('Branch name cannot be empty');
        return;
    }

    // 🔥 Get token from storage
    const token = getAuthToken();
    
    if (!token) {
        setError('Authentication token not found. Please login again.');
        return;
    }

    setLoading(true);
    setError(null);

    try {
        if (editingBranchId) {
            // ✅ Edit branch - include token in URL
            const url = `https://newerp.awasthivitality.com/api/branch/update/${editingBranchId}?branch_name=${encodeURIComponent(branchName)}&token=${encodeURIComponent(token)}`;
            const response = await fetch(url, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error('Failed to update branch');
            }

            // ✅ Refresh list after successful update
            await fetchBranches();
        } else {
            // ✅ Add new branch - include token in URL
            const url = `https://newerp.awasthivitality.com/api/branch/store?branch_name=${encodeURIComponent(branchName)}&token=${encodeURIComponent(token)}`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error('Failed to add branch');
            }

            const data = await response.json();
            const newBranch = data.branch;

            setBranches((prev) => [
                ...prev,
                {
                    id: newBranch.branch_id,
                    name: newBranch.branch_name,
                },
            ]);
        }

        closeModal();
    } catch (err) {
        console.error(err);
        setError(err.message || 'Something went wrong');
    } finally {
        setLoading(false);
    }
};


    // Delete handler
    const handleDelete = async (id) => {
        const confirmDelete = window.confirm('Are you sure you want to delete this branch?');
        if (!confirmDelete) return;

        setLoading(true);
        setError(null);

        try {
            const token = getAuthToken();
            const url = token 
                ? `https://newerp.awasthivitality.com/api/branch/delete/${id}?token=${encodeURIComponent(token)}`
                : `https://newerp.awasthivitality.com/api/branch/delete/${id}`;
            
            const response = await fetch(url, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error('Failed to delete branch');
            }

            // Remove deleted branch from state
            setBranches(prev => prev.filter(branch => branch.id !== id));
        } catch (err) {
            console.error(err);
            setError(err.message || 'Failed to delete branch');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="branch-page-container">
            <div className="branch-header">
                <h2>🏠 Branch List</h2>
                <button className="add-button" onClick={openAddModal}>
                    ➕ Add Branch
                </button>
            </div>

            <div className="table-container">
                <table className="branch-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Branch Name</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                   <tbody>
    {branches.length > 0 ? (
        branches.map((branch, index) => (
            <tr key={branch.id}>
                {/* Sequence Number */}
                <td>{index + 1}</td> 
                
                {/* Branch Name */}
                <td>{branch.name}</td>
                
                <td>
                    <button
                        className="edit-button"
                        onClick={() => openEditModal(branch)}
                    >
                        ✏️ Edit
                    </button>
                    <button
                        className="delete-button"
                        onClick={() => handleDelete(branch.id)}
                        style={{ marginLeft: '8px' }}
                    >
                        🗑️ Delete
                    </button>
                </td>
            </tr>
        ))
    ) : (
        <tr>
            <td colSpan="3" style={{ textAlign: 'center' }}>
                {error || 'No branches available.'}
            </td>
        </tr>
    )}
</tbody>

                </table>
            </div>

            {/* Modal for Add/Edit */}
            {showModal && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>{editingBranchId ? 'Edit Branch' : 'Add New Branch'}</h3>
                        <form onSubmit={handleSubmit}>
                            <input
                                type="text"
                                placeholder="Branch Name"
                                value={branchName}
                                onChange={(e) => setBranchName(e.target.value)}
                                disabled={loading}
                                autoFocus
                            />
                            {error && <p className="error-message">{error}</p>}
                            <div className="modal-buttons">
                                <button type="submit" disabled={loading}>
                                    {loading
                                        ? editingBranchId
                                            ? 'Updating...'
                                            : 'Adding...'
                                        : editingBranchId
                                            ? 'Update'
                                            : 'Add '}
                                </button>
                                <button type="button" onClick={closeModal} disabled={loading}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Branch;